import { isEqual, map } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import EcUserNameSelectValueWithControls from '~/components/Shared/EcUserNameSelectValueWithControls/EcUserNameSelectValueWithControls';
import UserDepartmentSelect from '~/components/Shared/UserDepartmentSelect/UserDepartmentSelect';
import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { EntityType } from '~/enums/EntityType';

import { black2 } from '../../../assets/shared-styles/general';
import EcCheckbox from '../../Shared/EcCheckbox/EcCheckbox';
import EcDepartmentSelectValueWithControls from '../../Shared/EcDepartmentSelectValueWithControls/EcDepartmentSelectValueWithControls';
import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import EcRadioButton from '../../Shared/EcRadioButton/EcRadioButton';
import EcRadioContainer from '../../Shared/EcRadioContainer/EcRadioContainer';
import WarningIcon from '../../Shared/Icons/WarningIcon';
import styles from './AccessSettingsModal.styles';

class AccessSettingsModal extends Component {
  constructor(props) {
    super(props);

    this.handleAllowUserDownloadChange =
      this.handleAllowUserDownloadChange.bind(this);
    this.handleActionClick = this.handleActionClick.bind(this);
    this.updateUserPrivilege = this.updateUserPrivilege.bind(this);
    this.updateDepartmentPrivilege = this.updateDepartmentPrivilege.bind(this);
    this.handleRemoveDepartment = this.handleRemoveDepartment.bind(this);
    this.handleChangeUserAndDepartmentSelection =
      this.handleChangeUserAndDepartmentSelection.bind(this);

    this.state = {
      itemName: this.props.itemsCurrentName,
      folderId: this.props.folderId,
      deptAccessList: this.processDepartments(this.props.deptAccessList) || [],
      folderAccessList: this.props.folderAccessList || [],
      selectedOption: this.renderSelectedOption(
        this.props.deptAccessList,
        this.props.folderAccessList,
        this.props.visibilityLevel,
      ),
      allowUsersDownloadChecked:
        this.props.visibilityLevel === 'OPEN_NO_EDIT' ? true : false,
      userNameSearchResult: [],
      departmentsSelected: this.props.deptAccessList,
      userDepartmentValues: this.transformAccessListsIntoUserDepartmentValue(
        this.props.deptAccessList,
        this.props.folderAccessList || [],
      ),
    };
  }

  componentDidMount() {
    const { onGetFormDataRef } = this.props;
    if (onGetFormDataRef) {
      onGetFormDataRef({
        handleActionClick: () => this.handleActionClick(),
      });
    }
  }

  componentWillUnmount() {
    const { onGetFormDataRef } = this.props;
    if (onGetFormDataRef) {
      onGetFormDataRef(null);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { onGetFormDataRef } = this.props;
    if (onGetFormDataRef) {
      onGetFormDataRef({
        handleActionClick: () => this.handleActionClick(),
      });
    } else if (prevProps.onGetFormDataRef) {
      prevProps.onGetFormDataRef(null);
    }

    /**
     * Update the value for the UserDepartmentSelect only when the actual user or departments are added or removed
     * Changes to permissions should not trigger a reload in UserDepartmentSelect
     */
    const areUserIdsEqual = isEqual(
      map(prevState.folderAccessList, 'user_id'),
      map(this.state.folderAccessList, 'user_id'),
    );
    const areDepartmentIdsEqual = isEqual(
      map(prevState.departmentsSelected, 'department_id'),
      map(this.state.departmentsSelected, 'department_id'),
    );

    if (!areUserIdsEqual || !areDepartmentIdsEqual) {
      this.setState({
        userDepartmentValues: this.transformAccessListsIntoUserDepartmentValue(
          this.state.departmentsSelected,
          this.state.folderAccessList,
        ),
      });
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.departmentsSelected !== prevState.departmentsSelected) {
      return { departmentsSelected: nextProps.departmentsSelected };
    } else {
      return null;
    }
  }

  processDepartments(departments) {
    return departments && departments.length
      ? departments.map((dept) => {
          return {
            id: dept.department_id,
            department_id: dept.department_id,
            department_name: dept.department_name,
            label: dept.department_name,
            value: dept.department_name,
            name: dept.department_name,
            visibility_level: dept.visibility_level || 'OPEN',
          };
        })
      : [];
  }

  transformAccessListsIntoUserDepartmentValue(
    departmentsSelected,
    folderAccessList,
  ) {
    const departmentValues = departmentsSelected
      ? departmentsSelected.map((dept) => ({
          id: Number(dept.department_id),
          type: EntityType.Department,
          role: null,
          name: dept.department_name,
        }))
      : [];
    const userValues = folderAccessList
      ? folderAccessList.map((user) => ({
          id: user.user_id,
          type: EntityType.User,
          role: null,
          name: user.user_name,
        }))
      : [];

    return [...departmentValues, ...userValues];
  }

  renderSelectedOption(deptAccessList, folderAccessList, visibilityLevel) {
    return visibilityLevel === 'RESTRICTED'
      ? 'specificViewEdit'
      : visibilityLevel === 'OPEN_NO_EDIT' ||
          visibilityLevel === 'OPEN_NO_EDIT_DOWNLOAD'
        ? 'everyoneView'
        : visibilityLevel === 'OPEN'
          ? 'everyoneViewEdit'
          : 'inherited';
  }

  handleInputChange(itemName) {
    this.setState({ itemName });
  }

  handleInputFocus(event) {
    event.target.select();
  }

  handleAllowUserDownloadChange() {
    this.setState((prevState) => ({
      allowUsersDownloadChecked: !prevState.allowUsersDownloadChecked,
    }));
  }

  renderAllowUsersToDownload() {
    const { classes, intl } = this.props;
    const { allowUsersDownloadChecked } = this.state;

    return (
      <div className={classes.allowUsersDownloadContainer}>
        <div className={classes.allowUsersDownloadHeader}>
          {intl.formatMessage({
            id: 'AccessSettingsModal.additionalAccessSettings',
            defaultMessage: 'ADDITIONAL ACCESS SETTINGS',
            description: 'Additional access settings header',
          })}
        </div>
        <span className={classes.privilegeOptionContainer}>
          <EcCheckbox
            id="allowUsersDownloadCheckBox"
            checked={allowUsersDownloadChecked}
            onClick={this.handleAllowUserDownloadChange}
          />
          <label htmlFor="allowUsersDownloadCheckBox">
            {intl.formatMessage({
              id: 'AccessSettingsModal.allowUsersDownload',
              defaultMessage: 'Allow users to download',
              description: 'Allow users to download checkbox label',
            })}
          </label>
        </span>
      </div>
    );
  }

  handleOptionSelection(selectedValue) {
    const { allowUsersDownloadChecked } = this.state;

    this.setState({
      selectedOption: selectedValue,
      allowUsersDownloadChecked:
        selectedValue !== 'everyoneView' ? false : allowUsersDownloadChecked,
    });
  }

  renderAccessSettingsOptions() {
    const { classes, intl, user, folderId } = this.props;
    const { selectedOption } = this.state;

    const isRootFolder = folderId === user?.root_folder;

    return (
      <EcRadioContainer
        role="radiogroup"
        groupName="accessSettingsOptions"
        onChange={({ selectedValue }) =>
          this.handleOptionSelection(selectedValue)
        }
        defaultValue={selectedOption}
      >
        {(groupName, selectedValue, onOptionChange) => (
          <div className={classes.radioContainerContent}>
            {!isRootFolder && (
              <EcRadioButton
                id="inherited"
                groupName="accessSettingsOptions"
                value="inherited"
                label={
                  <span>
                    {intl.formatMessage({
                      id: 'AccessSettingsModal.inheritAccessSettings',
                      defaultMessage:
                        'Always inherit the access settings of the parent folder',
                      description: 'Inherit access settings radio label',
                    })}
                  </span>
                }
                onChange={onOptionChange}
                selectedValue={selectedOption}
                fontSize="13px"
                labelColor={black2}
                role="radio"
              />
            )}
            {selectedOption === 'inherited' && this.renderSelectionMessage()}

            <EcRadioButton
              id="everyoneViewEdit"
              groupName="accessSettingsOptions"
              value="everyoneViewEdit"
              label={
                <span>
                  {intl.formatMessage(
                    {
                      id: 'AccessSettingsModal.everyoneViewEdit',
                      defaultMessage:
                        'Allow <strong>everyone</strong> to <strong>view and edit</strong> this folder and its contents',
                      description: 'Everyone view and edit radio label',
                    },
                    {
                      strong: (chunks) => <b>{chunks}</b>,
                    },
                  )}
                </span>
              }
              onChange={onOptionChange}
              selectedValue={selectedOption}
              fontSize="13px"
              labelColor={black2}
              role="radio"
            />

            <EcRadioButton
              id="everyoneView"
              groupName="accessSettingsOptions"
              value="everyoneView"
              label={
                <span>
                  {intl.formatMessage(
                    {
                      id: 'AccessSettingsModal.everyoneView',
                      defaultMessage:
                        'Allow <strong>everyone</strong> to <strong>view</strong> this folder and its contents',
                      description: 'Everyone view radio label',
                    },
                    {
                      strong: (chunks) => <b>{chunks}</b>,
                    },
                  )}
                </span>
              }
              onChange={onOptionChange}
              selectedValue={selectedOption}
              fontSize="13px"
              labelColor={black2}
              role="radio"
            />

            {selectedOption === 'everyoneView' &&
              this.renderAllowUsersToDownload()}

            <EcRadioButton
              id="specificViewEdit"
              groupName="accessSettingsOptions"
              value="specificViewEdit"
              label={
                <span>
                  {intl.formatMessage(
                    {
                      id: 'AccessSettingsModal.specificUsersViewEdit',
                      defaultMessage:
                        'Allow <strong>only specific users</strong> to <strong>view and edit</strong> this folder and its contents',
                      description: 'Specific users view and edit radio label',
                    },
                    {
                      strong: (chunks) => <b>{chunks}</b>,
                    },
                  )}
                </span>
              }
              onChange={onOptionChange}
              selectedValue={selectedOption}
              fontSize="13px"
              labelColor={black2}
              role="radio"
            />

            {selectedOption === 'specificViewEdit' &&
              this.renderSelectionMessage()}
          </div>
        )}
      </EcRadioContainer>
    );
  }

  handleChangeUserAndDepartmentSelection(values) {
    const { updateDepartmentsArray } = this.props;
    const { departmentsSelected, folderAccessList } = this.state;
    const departments = [];
    const users = [];
    values.forEach((value) => {
      switch (value.type) {
        case EntityType.Department:
          const selectedDept = departmentsSelected.find(
            (dept) => dept.department_id === value.id,
          );
          departments.push({
            department_id: value.id,
            department_name: value.name,
            visibility_level: selectedDept?.visibility_level ?? 'OPEN',
          });
          break;
        case EntityType.User:
          const selectedUser = folderAccessList.find(
            (user) => user.user_id === value.id,
          );
          users.push({
            user_id: value.id,
            user_name: value.name,
            visibility_level: selectedUser?.visibility_level ?? 'OPEN',
          });
          break;
        default:
          break;
      }
    });

    this.setState({
      departmentsSelected: departments,
      folderAccessList: users,
    });
    updateDepartmentsArray(departments);
  }

  renderAccessSettings() {
    const { classes, folderId, intl } = this.props;
    const { selectedOption, userDepartmentValues } = this.state;

    if (folderId) {
      return (
        <div className={classes.folderAccessSettingContainer}>
          <div className={classes.headerText}>
            {intl.formatMessage({
              id: 'AccessSettingsModal.accessSettings',
              defaultMessage: 'ACCESS SETTINGS',
              description: 'Access settings header',
            })}
          </div>
          {this.renderAccessSettingsOptions()}
          {selectedOption === 'specificViewEdit' ||
          selectedOption === 'inherited' ? (
            <div className={classes.userDepartmentContainer}>
              <FormField
                label={intl.formatMessage({
                  id: 'AccessSettingsModal.addUsersAndDepartments',
                  defaultMessage: 'Add users and departments',
                  description: 'Add users and departments field label',
                })}
                input={UserDepartmentSelect}
                value={userDepartmentValues}
                onChange={this.handleChangeUserAndDepartmentSelection}
                inputProps={{ enablePortal: true }}
              />
              {this.renderDepartmentsSelection()}
              {this.renderUserNamesSelected()}
            </div>
          ) : null}
        </div>
      );
    }
  }

  renderSelectionMessage() {
    const { classes } = this.props;
    const { selectedOption } = this.state;

    if (
      selectedOption !== 'specificViewEdit' &&
      selectedOption !== 'inherited'
    ) {
      return null;
    }
    let aclUsageMessage =
      selectedOption === 'specificViewEdit' ? (
        <div className={classes.aclRestrictedControlsMessage}>
          <FormattedMessage
            id="AccessSettingsModal.adminUsersFullAccess"
            defaultMessage="Admin users always have full access to all folders.{br} Use the dropdowns to also give access to certain departments and power users."
            description="Admin users full access message"
            values={{
              br: <br />,
            }}
          />
        </div>
      ) : (
        <>
          <div className={classes.aclInheritedControlsMessage}>
            <WarningIcon size="20" />
            <span>
              <FormattedMessage
                id="AccessSettingsModal.inheritedAccessWarning"
                defaultMessage={`All users that have access to the parent folder will have the same level of access to this folder. <strong>If a user has Edit access to the parent folder, they will also have Edit access to this folder.</strong>{br}{br}Grant access to additional users below.`}
                description="Inherited access warning message."
                values={{
                  strong: (chunks) => <strong>{chunks}</strong>,
                  br: <br />,
                }}
              />
            </span>
          </div>
        </>
      );

    return (
      <div className={classes.aclControlsMessageBox}>{aclUsageMessage}</div>
    );
  }

  renderDepartmentsSelection() {
    const { classes, intl } = this.props;
    const { departmentsSelected } = this.state;
    const departmentLists =
      departmentsSelected && departmentsSelected.length
        ? departmentsSelected.map((list) => {
            return (
              <EcDepartmentSelectValueWithControls
                dept={list}
                key={`${list.department_name}-${list.department_id}`}
                updateDepartmentPrivilege={this.updateDepartmentPrivilege}
                closeHandler={() => this.handleRemoveDepartment(list)}
              />
            );
          })
        : null;

    return (
      <div
        className={
          !departmentsSelected
            ? classes.accessContainerEmpty
            : classes.accessDepartmentsContainer
        }
      >
        <div className={classes.inputFieldHeader}>
          {intl.formatMessage({
            id: 'AccessSettingsModal.departmentsHeader',
            defaultMessage: 'Departments',
            description: 'Departments section header',
          })}
        </div>
        {!departmentsSelected || !departmentsSelected.length ? (
          <p className={classes.noAccessMessage}>
            {intl.formatMessage({
              id: 'AccessSettingsModal.noDepartmentSelected',
              defaultMessage: 'No department has been selected.',
              description: 'No department selected message',
            })}
          </p>
        ) : (
          departmentLists
        )}
      </div>
    );
  }

  renderUserNamesSelected() {
    const { classes, intl } = this.props;
    const { folderAccessList } = this.state;
    const selectedUsers =
      folderAccessList && folderAccessList.length
        ? folderAccessList.map((user) => ({
            user_id: user.user_id,
            user_name: user.user_name,
            label: user.user_name,
            value: user.user_name,
            folder_id: user.folder_id,
            visibility_level: user.visibility_level || 'OPEN',
          }))
        : [];

    return (
      <div
        className={
          !selectedUsers
            ? classes.accessContainerEmpty
            : classes.accessUserContainer
        }
      >
        <div className={classes.inputFieldHeader}>
          {intl.formatMessage({
            id: 'AccessSettingsModal.usersHeader',
            defaultMessage: 'Users',
            description: 'Users section header',
          })}
        </div>
        {selectedUsers?.length > 0 ? (
          selectedUsers.map((user) => (
            <EcUserNameSelectValueWithControls
              accountUser={user}
              key={`${user.value}`}
              updateUserPrivilege={this.updateUserPrivilege}
              closeHandler={() => this.handleRemoveUser(user)}
            />
          ))
        ) : (
          <p className={classes.noAccessMessage}>
            {intl.formatMessage({
              id: 'AccessSettingsModal.noUsersSelected',
              defaultMessage: 'No users have been selected.',
              description: 'No users selected message',
            })}
          </p>
        )}
      </div>
    );
  }

  handleRemoveDepartment(dept) {
    const { updateDepartmentsArray } = this.props;
    const { departmentsSelected } = this.state;
    const newSelectedDepartments = departmentsSelected.filter(
      (account) => account.department_id !== dept.department_id,
    );

    this.setState({ departmentsSelected: newSelectedDepartments });
    updateDepartmentsArray(newSelectedDepartments);
  }

  updateDepartmentPrivilege(deptAndAccess) {
    const { departmentsSelected } = this.state;

    if (deptAndAccess) {
      const deptAccessList = departmentsSelected.map((account) =>
        Object.assign(
          account,
          [deptAndAccess].find(
            (dept) => dept.department_name === account.department_name,
          ),
        ),
      );

      this.setState({ deptAccessList });
    }
  }

  updateUserPrivilege(userAndAccess) {
    const { folderAccessList } = this.state;
    if (userAndAccess) {
      this.setState({
        folderAccessList: folderAccessList.map((user) => {
          if (user.user_id === userAndAccess.user_id) {
            return {
              ...user,
              visibility_level: userAndAccess.visibility_level,
            };
          }
          return user;
        }),
      });
    }
  }

  handleRemoveUser(removedUser) {
    const { folderAccessList } = this.state;

    this.setState({
      folderAccessList: folderAccessList.filter(
        (user) => user.user_id !== removedUser.user_id,
      ),
    });
  }

  handleActionClick() {
    const {
      itemsParentFolderId,
      folderId,
      handleNameChange,
      hideModal,
      client,
    } = this.props;
    const {
      itemName,
      selectedOption,
      allowUsersDownloadChecked,
      folderAccessList,
      departmentsSelected,
    } = this.state;

    const visibilityLevel =
      selectedOption === 'everyoneViewEdit' && !allowUsersDownloadChecked
        ? 'OPEN'
        : selectedOption === 'everyoneView' && allowUsersDownloadChecked
          ? 'OPEN_NO_EDIT'
          : selectedOption === 'specificViewEdit'
            ? 'RESTRICTED'
            : selectedOption === 'inherited'
              ? null
              : 'OPEN_NO_EDIT_DOWNLOAD';

    const deptAccessList = departmentsSelected.map((item) => {
      return {
        department_id: item.department_id,
        department_name: item.department_name,
        visibility_level: item.visibility_level || 'OPEN',
      };
    });

    const clientId = client;

    const promise = folderId
      ? handleNameChange(
          folderId,
          itemName,
          itemsParentFolderId,
          visibilityLevel,
          folderAccessList,
          deptAccessList,
          clientId,
        )
      : handleNameChange(
          itemName,
          itemsParentFolderId,
          folderAccessList,
          deptAccessList,
        );

    hideModal();

    return promise;
  }

  render() {
    const {
      classes,
      title,
      id,
      labelText,
      confirmButtonText,
      hideModal,
      folderId,
      intl,
      hideFooter,
    } = this.props;
    const { itemName } = this.state;

    const content = (
      <div className={classes.modalBody} id={id}>
        <div className={classes.modalBodyContent}>
          {!folderId && (
            <div>
              <div className={classes.headerText}>{labelText}</div>
              <input
                data-testid="folder-name-input"
                className={classes.folderName}
                value={itemName}
                onChange={(event) => {
                  this.handleInputChange(event.target.value);
                }}
                onFocus={(event) => {
                  this.handleInputFocus(event);
                }}
                title={`${title} name`}
              />
            </div>
          )}
          {this.renderAccessSettings()}
        </div>
      </div>
    );

    if (hideFooter) {
      return content;
    }

    return (
      <EcModalCard
        title={title}
        content={content}
        footer={
          <>
            <Button
              variant="tertiary"
              text={intl.formatMessage({
                id: 'AccessSettingsModal.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              })}
              onClick={hideModal}
            />
            <Button
              disabled={!itemName}
              variant="primary"
              text={confirmButtonText}
              onClick={this.handleActionClick}
            />
          </>
        }
        hideModal={hideModal}
      />
    );
  }
}

AccessSettingsModal.propTypes = {
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  labelText: PropTypes.string,
  id: PropTypes.string,
  itemsParentFolderId: PropTypes.number,
  itemsCurrentName: PropTypes.string.isRequired,
  confirmButtonIcon: PropTypes.object,
  confirmButtonText: PropTypes.string.isRequired,
  editItemName: PropTypes.func,
  createItemName: PropTypes.func,
  hideModal: PropTypes.func.isRequired,
};

export default injectSheet(styles)(injectIntl(AccessSettingsModal));
